import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import magic from '../lib/magic';

const Success = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session_id');

        if (sessionId) {
          const didToken = await magic.user.getIdToken();
          const response = await fetch(`/api/user/orders/${sessionId}`, {
            headers: {
              'Authorization': `Bearer ${didToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch order details');
          }

          const data = await response.json();
          console.log('Order details:', data.order); // For debugging
          setOrder(data.order);
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF] flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF] py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8"
        >
          {/* Order Success Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text">
              Order Confirmed!
            </h1>
            <p className="text-gray-600 mt-2">
              Order #{order?.orderNumber}
            </p>
          </div>

          {order && (
            <div className="space-y-8">
              {/* Shipping Information */}
              <div className="border-t border-b border-gray-200 py-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">Shipping Address</h3>
                    <div className="text-gray-600">
                      <p>{order.shipping?.name}</p>
                      <p>{order.shipping?.address?.line1}</p>
                      {order.shipping?.address?.line2 && (
                        <p>{order.shipping.address.line2}</p>
                      )}
                      <p>
                        {order.shipping?.address?.city}
                        {order.shipping?.address?.state && `, ${order.shipping.address.state}`}
                        {' '}
                        {order.shipping?.address?.postal_code}
                      </p>
                      <p>{order.shipping?.address?.country}</p>
                      {order.shipping?.phone && (
                        <p className="mt-2">📱 {order.shipping.phone}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3">Shipping Method</h3>
                    <p className="text-gray-600">{order.shipping?.method}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Estimated delivery: 3-7 business days
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-4">Order Summary</h3>
                <div className="space-y-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex gap-4 border-b border-gray-100 pb-4">
                      <div className="w-24 h-24 flex-shrink-0">
                        <img
                          src={item.imageData || item.imageUrl}
                          alt={`Order item ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">AI Generated Poster</p>
                        <p className="text-sm text-gray-500">Size: {item.size}</p>
                        <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                        <p className="text-sm text-gray-500 mt-1">Prompt: {item.prompt}</p>
                        <p className="text-sm font-medium mt-1">£{item.price.toFixed(2)} each</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>£{order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>£{order.shipping?.cost?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>£{order.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Next Steps */}
              <div className="text-center space-y-4">
                <p className="text-gray-600">
                  We've sent a confirmation email to {order.customerEmail}
                </p>
                <Link
                  to="/generator"
                  className="inline-block px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white rounded-full hover:shadow-lg transition-all duration-300"
                >
                  Create More Posters
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Success; 